import {
  makeTemplatePlacement,
  type PdfEditorFieldBlockTemplate,
  type PdfEditorState,
} from '@urbanx/agx.ui-components2';
import { nswContractExchangeDocuSignAnchors } from '../ContractReviewAndSign/contractExchangeDocuSignAnchors';

// Keep template and field IDs stable so previously saved mappings can be reopened.
export const contractReplacementFieldBlockTemplates: PdfEditorFieldBlockTemplate[] = [
  {
    id: 'contract-upload-contract-date',
    label: 'Contract Date',
    allowMultiple: false,
    allowFieldDeletion: true,
    initialPlacement: {
      pageIndex: 0,
      // Align with the date row in the 2026 NSW contract.
      x: 200,
      y: 957,
      referencePageWidth: 945,
      referencePageHeight: 1336,
    },
    fields: [
      {
        id: 'contract-date',
        label: 'Contract Date',
        exportName: 'ContractDateAnchor',
        positionAnchorText: 'NSWCECONTRACTDATEPOSITION',
        x: 0,
        y: 0,
        width: 200,
        height: 18,
      },
    ],
  },
  {
    id: 'contract-upload-signatures',
    label: 'Contract Upload Signatures',
    buttonLabel: 'Add Signature Fields',
    allowMultiple: false,
    allowFieldDeletion: true,
    initialPlacement: {
      pageIndex: 1,
      x: 65,
      y: 230,
      referencePageWidth: 945,
      referencePageHeight: 1336,
    },
    fields: [
      {
        id: 'seller',
        label: 'Vendor',
        exportPrefix: 'Sellers',
        signatureAnchor: nswContractExchangeDocuSignAnchors.sellerSignature,
        x: 0,
      },
      {
        id: 'buyer',
        label: 'Buyer',
        exportPrefix: 'Buyers',
        signatureAnchor: nswContractExchangeDocuSignAnchors.buyerSignature,
        x: 417,
      },
    ].flatMap((party) =>
      // Individual signatories precede company signatories for each party.
      [
        { x: 0, y: 0, width: 160 },
        { x: 0, y: 128, width: 160 },
        { x: 195, y: 0, width: 160 },
        { x: 195, y: 128, width: 160 },
        { x: 0, y: 444, width: 180 },
        { x: 210, y: 444, width: 180 },
      ].map((placement, index) => {
        const isCompany = index >= 4;
        const number = isCompany ? index - 3 : index + 1;
        return {
          id: `${party.id}-${isCompany ? 'company-' : ''}${number}-signature`,
          label: `${party.label} ${isCompany ? 'Company ' : ''}${number} Signature`,
          exportName: `${party.exportPrefix}${index + 1}SignatureSignHere`,
          docusignAnchorText: party.signatureAnchor(index + 1),
          ...placement,
          x: party.x + placement.x,
          height: 22,
        };
      })
    ),
  },
];

export const initialContractReplacementEditState: PdfEditorState = {
  version: 1,
  textBoxes: [],
  strikeOuts: [],
  fieldBlocks: contractReplacementFieldBlockTemplates.map((template) => ({
    id: template.id,
    templateId: template.id,
    ...makeTemplatePlacement(template),
  })),
};

export const getContractReplacementPdfConfiguration = (pageCount: number) => {
  if (pageCount < 1) throw new Error('The contract PDF has no pages.');
  const lastPageIndex = pageCount - 1;
  return {
    fieldBlockTemplates: contractReplacementFieldBlockTemplates.map((template) => ({
      ...template,
      initialPlacement: {
        ...template.initialPlacement,
        pageIndex: Math.min(template.initialPlacement.pageIndex, lastPageIndex),
      },
    })),
    editState: {
      ...initialContractReplacementEditState,
      fieldBlocks: initialContractReplacementEditState.fieldBlocks.map((block) => ({
        ...block,
        pageIndex: Math.min(block.pageIndex, lastPageIndex),
      })),
    },
  };
};
